// Mock data constants — enum arrays and curated value pools
// These mirror the type definitions in @shared/types/ to keep generators in sync

// ── ID Prefixes ─────────────────────────────────────────────────────────────

export const ID_PREFIX = {
  campaign: 'camp_',
  enrollment: 'enr_',
  customer: 'cust_',
  competitor: 'comp_',
  opportunity: 'opp_',
  payment: 'pay_',
  order: 'ORD-',
  terminal: 'tmr_',
  dispute: 'dp_',
  authorization: 'auth_',
  refund: 're_',
  merchant: 'merch_',
  bank: 'BNK-',
  organization: 'ORG-',
  application: 'APP-',
  cardProgram: 'CP-',
  programProduct: 'PP-',
  billingSchedule: 'sched_',
  mmAccount: 'ACC-',
  mmReserve: 'RES-',
  mmOperation: 'OPS-',
  mmPayout: 'PAY-',
  mmAdjustment: 'ADJ-',
  mmDeposit: 'DEP-',
  mmCollectedFee: 'FEE-',
  tariff: 'TAR-',
  fee: 'FS-',
  fxRate: 'FXR-',
  fxSchedule: 'FXS-',
  rail: 'RAIL-',
  alert: 'alert_',
  review: 'rev_',
  disputeRail: 'DR-',
  reconciliation: 'REC-',
  settlement: 'STL-',
  clearing: 'CLR-',
  reserveProfile: 'RSP-',
  payoutProfile: 'PSP-',
} as const

// ── Campaign Domain ─────────────────────────────────────────────────────────

export const CAMPAIGN_STATUSES = [
  'draft', 'scheduled', 'active', 'paused', 'completed', 'archived',
] as const

export const OBJECTIVE_TYPES = [
  'increase_frequency', 'boost_aov', 'reduce_churn', 'drive_referrals',
  'new_customer_acquisition', 'product_adoption', 'seasonal_promotion',
  'win_back', 'vip_retention', 'cross_sell',
] as const

export const REWARD_MECHANICS = [
  'points_multiplier', 'cashback', 'discount_percentage', 'discount_fixed',
  'free_item', 'tiered_reward', 'referral_bonus', 'early_access',
  'milestone_bonus', 'loyalty_tier_upgrade',
] as const

export const CAMPAIGN_CHANNELS = ['email', 'sms', 'push', 'in_app', 'pos'] as const

export const ENROLLMENT_STATUSES = ['pending', 'active', 'suspended', 'opted_out'] as const

export const ENROLLMENT_CHANNELS = ['in_store', 'online', 'app', 'referral', 'import', 'api'] as const

export const TIERS = ['Bronze', 'Silver', 'Gold', 'Platinum'] as const

// ── Customer Domain ─────────────────────────────────────────────────────────

export const CUSTOMER_STATUSES = [
  'active', 'risky', 'in_review', 'pending_review',
  'restricted', 'suspended', 'terminated', 'closed',
] as const

export const RISK_LEVELS = ['low', 'medium', 'high'] as const

export const CUSTOMER_TYPES = ['individual', 'business'] as const

export const PAYMENT_METHODS = [
  'Credit Card', 'PayPal', 'Bank Transfer', 'Apple Pay', 'Google Pay',
] as const

// ── Competitive Domain ──────────────────────────────────────────────────────

export const CUSTOMER_SEGMENTS = [
  'all', 'new_customers', 'active_members', 'vip', 'at_risk', 'lapsed',
] as const

export const EFFORT_LEVELS = ['low', 'medium', 'high'] as const
export const IMPACT_LEVELS = ['low', 'medium', 'high'] as const

export const OPPORTUNITY_STATUSES = [
  'identified', 'in_progress', 'completed', 'dismissed',
] as const

export const TREND_DIRECTIONS = ['up', 'down', 'stable'] as const

// ── Payment Domain ──────────────────────────────────────────────────────────

export const PAYMENT_STATUSES = [
  'pending', 'succeeded', 'failed', 'canceled', 'refunded',
  'uncaptured', 'approved', 'processing', 'declined', 'blocked',
  'expired', 'partially_refunded',
] as const

export const PAYMENT_CHANNELS = [
  'terminal', 'ecommerce', 'link',
] as const

export const CHANNEL_ID_PREFIXES: Record<string, string> = {
  terminal: 'trm_',
  ecommerce: 'ecom_',
  link: 'lnk_',
} as const

export const CARD_BRANDS = ['Visa', 'Mastercard', 'Amex', 'Discover'] as const

export const PAYMENT_METHOD_TYPES = [
  'card', 'bank_transfer', 'wallet', 'bnpl',
] as const

export const PAYMENT_DESCRIPTIONS = [
  'Online purchase', 'Subscription renewal', 'In-store payment',
  'Recurring billing', 'One-time charge', 'Service fee',
  'Product order', 'Membership fee', 'Digital download',
  'Marketplace transaction', 'Invoice payment', 'Deposit',
] as const

// ── Order Domain ────────────────────────────────────────────────────────────

export const ORDER_STATUSES = [
  'pending', 'confirmed', 'fulfilled', 'cancelled', 'returned',
] as const

export const STORE_NAMES = [
  'Downtown Coffee Co.', 'Bakery Delights', 'Tech Plaza', 'Green Grocers',
  'Fashion Forward', 'Garden Center', 'Fit Life Store', 'Home Essentials',
  'Book Haven', 'Artisan Market', 'Gourmet Kitchen', 'Sports Outlet',
  'Pet Paradise', 'Music World', 'Beauty Bar',
] as const

export const ORDER_DESCRIPTIONS = [
  'Premium Coffee Bean Selection', 'Artisan Pastry Assortment',
  'Electronic Gadgets Bundle', 'Organic Vegetable Box',
  'Designer Clothing Set', 'Home Garden Tools', 'Fitness Equipment Package',
  'Kitchen Appliance Set', 'Book Collection', 'Handmade Jewelry Set',
  'Specialty Tea Collection', 'Smart Home Starter Kit',
  'Outdoor Adventure Gear', 'Music Instrument Accessories',
  'Skincare Essentials Kit',
] as const

// ── Terminal Domain ─────────────────────────────────────────────────────────

export const TERMINAL_STATUSES = ['active', 'inactive', 'offline', 'blocked'] as const

export const TERMINAL_MODELS = [
  'Verifone P400', 'Verifone V240m', 'Ingenico Desk/5000',
  'Ingenico Move/5000', 'PAX A920', 'PAX A80', 'Clover Flex',
  'Clover Mini', 'Square Terminal', 'Sunmi P2',
] as const

export const TERMINAL_TYPES = [
  'Countertop', 'Mobile', 'Portable', 'Self-service', 'Kiosk', 'Integrated',
] as const

export const TERMINAL_LOCATIONS = [
  { name: 'Downtown Store', city: 'San Francisco', state: 'CA', country: 'USA' },
  { name: 'Mall Location', city: 'Los Angeles', state: 'CA', country: 'USA' },
  { name: 'Airport Terminal', city: 'Chicago', state: 'IL', country: 'USA' },
  { name: 'Suburban Branch', city: 'Houston', state: 'TX', country: 'USA' },
  { name: 'Pop-up Shop', city: 'New York', state: 'NY', country: 'USA' },
  { name: 'Flagship Store', city: 'Seattle', state: 'WA', country: 'USA' },
  { name: 'University Campus', city: 'Boston', state: 'MA', country: 'USA' },
  { name: 'Central Station', city: 'Denver', state: 'CO', country: 'USA' },
  { name: 'Beach Front', city: 'Miami', state: 'FL', country: 'USA' },
  { name: 'Industrial Park', city: 'Portland', state: 'OR', country: 'USA' },
] as const

// ── Dispute Domain ──────────────────────────────────────────────────────────

export const DISPUTE_STATUSES = [
  'warning_needs_response', 'warning_under_review', 'warning_closed',
  'needs_response', 'under_review', 'charge_refunded', 'won', 'lost',
] as const

export const DISPUTE_REASONS = [
  'duplicate', 'fraudulent', 'subscription_canceled',
  'product_unacceptable', 'product_not_received', 'unrecognized',
  'credit_not_processed', 'general',
] as const

// ── Authorization / Rails Domain ────────────────────────────────────────────

export const AUTHORIZATION_STATUSES = [
  'approved', 'declined', 'referred', 'reversed', 'timed_out',
] as const

export const AUTHORIZATION_NETWORKS = ['visa', 'mastercard'] as const

export const AUTHORIZATION_RESPONSE_CODES = [
  { code: '00', meaning: 'Approved' },
  { code: '05', meaning: 'Do Not Honor' },
  { code: '14', meaning: 'Invalid Card Number' },
  { code: '41', meaning: 'Lost Card' },
  { code: '43', meaning: 'Stolen Card' },
  { code: '51', meaning: 'Insufficient Funds' },
  { code: '54', meaning: 'Expired Card' },
  { code: '57', meaning: 'Transaction Not Permitted' },
  { code: '61', meaning: 'Exceeds Withdrawal Limit' },
  { code: '65', meaning: 'Activity Count Limit Exceeded' },
] as const

export const RAIL_STATUSES = ['active', 'inactive', 'pending', 'suspended'] as const

export const RAIL_TYPES = ['card', 'ach', 'wire', 'sepa', 'swift', 'rtp', 'local'] as const

export const RAIL_REGIONS = [
  'North America', 'Europe', 'Asia Pacific', 'Latin America', 'Middle East', 'Global',
] as const

export const RAIL_PROVIDERS = [
  'Visa Direct', 'Mastercard Send', 'FedWire', 'SEPA Credit',
  'SWIFT gpi', 'RTP Network', 'ACH Network', 'Faster Payments',
] as const

export const ALERT_SEVERITIES = ['critical', 'warning', 'info'] as const

export const ALERT_TYPES = [
  'approval_rate_drop', 'clearing_rejection', 'settlement_mismatch',
  'network_connectivity', 'duplicate_detection', 'late_presentment',
  'dispute_threshold',
] as const

export const ALERT_STATUSES = ['new', 'acknowledged', 'investigating', 'resolved'] as const

export const REVIEW_TYPES = ['authorization', 'clearing', 'settlement'] as const
export const REVIEW_STATUSES = ['pending', 'in_progress', 'approved', 'rejected'] as const
export const REVIEW_PRIORITIES = ['high', 'medium', 'low'] as const

export const DISPUTE_RAIL_STATUSES = [
  'received', 'under_review', 'rebutted', 'won', 'lost',
  'pre_arbitration', 'arbitration', 'expired',
] as const

export const DISPUTE_RAIL_REASONS = [
  'fraud', 'not_as_described', 'not_received', 'duplicate',
  'credit_not_processed', 'authorization_issue', 'processing_error',
  'subscription_cancelled',
] as const

export const RECONCILIATION_STATUSES = [
  'matched', 'unmatched', 'partial', 'pending_review', 'resolved',
] as const

export const RECONCILIATION_TYPES = [
  'daily_settlement', 'interchange', 'fee_reconciliation',
] as const

// ── Refund Domain ───────────────────────────────────────────────────────────

export const REFUND_STATUSES = [
  'pending', 'succeeded', 'failed', 'canceled',
  'refund_processing', 'refund_declined',
] as const

export const REFUND_REASONS = [
  'requested_by_customer', 'duplicate_charge', 'fraudulent',
  'product_not_received', 'product_unacceptable', 'subscription_canceled',
  'order_change', 'general',
] as const

// ── Merchant Domain ─────────────────────────────────────────────────────────

export const MERCHANT_STATUSES = ['active', 'inactive', 'pending', 'suspended', 'blocked'] as const
export const MERCHANT_TYPES = ['individual', 'business', 'enterprise'] as const
export const MERCHANT_RISK_LEVELS = ['low', 'medium', 'high'] as const

export const MERCHANT_INDUSTRIES = [
  'Sporting Goods', 'Retail', 'Hospitality', 'Technology',
  'Food & Beverage', 'Healthcare', 'Education', 'Financial Services',
  'Transportation', 'Real Estate', 'Entertainment', 'E-commerce',
  'Professional Services', 'Manufacturing', 'Agriculture',
] as const

export const MERCHANT_NAMES = [
  'Alpine Sports GmbH', 'Lux Retail Ltd', 'NovaPay Solutions',
  'Café du Port SARL', 'TechVenture AB', 'Pacific Trade Co.',
  'Nordic Design Studio', 'Metro Fresh Markets', 'Golden Gate Electronics',
  'Sapphire Consulting', 'Emerald Health Systems', 'Pinnacle Logistics',
  'Azure Cloud Kitchen', 'Summit Outdoor Gear', 'Horizon Education Inc.',
] as const

// ── Bank & Organization Domain ──────────────────────────────────────────────

export const BANK_STATUSES = [
  'active', 'inactive', 'suspended', 'pending_approval', 'onboarding',
] as const

export const BANK_TYPES = [
  'issuing', 'acquiring', 'issuing_acquiring', 'processing',
] as const

export const BANK_NAMES = [
  { name: 'Nordea Bank', displayName: 'Nordea', legalName: 'Nordea Bank Abp', bic: 'NDEAFIHH', country: 'FI' },
  { name: 'Deutsche Bank', displayName: 'Deutsche', legalName: 'Deutsche Bank AG', bic: 'DEUTDEFF', country: 'DE' },
  { name: 'Barclays', displayName: 'Barclays', legalName: 'Barclays Bank PLC', bic: 'BARCGB22', country: 'GB' },
  { name: 'BNP Paribas', displayName: 'BNP', legalName: 'BNP Paribas SA', bic: 'BNPAFRPP', country: 'FR' },
  { name: 'ING Group', displayName: 'ING', legalName: 'ING Bank N.V.', bic: 'INGBNL2A', country: 'NL' },
  { name: 'Santander', displayName: 'Santander', legalName: 'Banco Santander SA', bic: 'BSCHESMM', country: 'ES' },
  { name: 'UniCredit', displayName: 'UniCredit', legalName: 'UniCredit S.p.A.', bic: 'UNCRITMM', country: 'IT' },
  { name: 'Swedbank', displayName: 'Swedbank', legalName: 'Swedbank AB', bic: 'SWEDSESS', country: 'SE' },
  { name: 'Danske Bank', displayName: 'Danske', legalName: 'Danske Bank A/S', bic: 'DABADKKK', country: 'DK' },
  { name: 'KBC Bank', displayName: 'KBC', legalName: 'KBC Bank NV', bic: 'KREDBEBB', country: 'BE' },
] as const

export const ORGANIZATION_TYPES = [
  'payment_facilitator', 'marketplace', 'iso', 'agent', 'reseller',
] as const

// ── Money Movement Domain ───────────────────────────────────────────────────

export const MM_ACCOUNT_STATUSES = ['active', 'frozen', 'closed', 'pending'] as const
export const MM_ACCOUNT_TYPES = ['operating', 'settlement', 'reserve', 'system', 'escrow', 'holding'] as const

export const MM_ADJUSTMENT_STATUSES = ['applied', 'pending', 'reversed', 'rejected'] as const
export const MM_ADJUSTMENT_TYPES = ['credit', 'debit', 'correction', 'fee-waiver', 'chargeback'] as const

export const MM_COLLECTED_FEE_STATUSES = ['collected', 'pending', 'failed', 'waived'] as const
export const MM_COLLECTED_FEE_TYPES = ['platform', 'processing', 'interchange', 'scheme', 'service', 'monthly'] as const

export const MM_DEPOSIT_STATUSES = ['completed', 'pending', 'in-transit', 'failed'] as const
export const MM_DEPOSIT_METHODS = ['wire', 'ach', 'sepa', 'internal'] as const

export const MM_OPERATION_STATUSES = ['completed', 'pending', 'failed', 'cancelled', 'processing'] as const
export const MM_OPERATION_TYPES = [
  'charge', 'refund', 'payout', 'adjustment', 'stripe-fee',
  'deposit', 'transfer', 'funding', 'withdrawal', 'fee-collection', 'settlement',
] as const

export const MM_PAYOUT_STATUSES = ['completed', 'pending', 'in-transit', 'failed', 'cancelled'] as const
export const MM_PAYOUT_METHODS = ['ach', 'wire', 'rtp', 'check', 'internal'] as const

export const MM_RESERVE_STATUSES = ['active', 'released', 'pending', 'expired'] as const
export const MM_RESERVE_TYPES = ['rolling', 'fixed', 'capped', 'minimum-balance'] as const

// ── Billing Admin Domain ────────────────────────────────────────────────────

export const TARIFF_STATUSES = ['active', 'inactive', 'draft', 'archived'] as const
export const TARIFF_MERCHANT_TYPES = ['standard', 'premium', 'enterprise', 'custom'] as const

export const FEE_STATUSES = ['active', 'draft', 'archived'] as const
export const FEE_TYPES = ['blended', 'variable', 'fixed'] as const

export const FX_RATE_STATUSES = ['active', 'expired', 'pending', 'suspended'] as const
export const FX_PROVIDERS = [
  'Reuters', 'Bloomberg', 'ECB', 'Oanda', 'XE', 'CurrencyLayer',
] as const

export const CURRENCY_PAIRS = [
  { source: 'USD', target: 'EUR' },
  { source: 'USD', target: 'GBP' },
  { source: 'EUR', target: 'GBP' },
  { source: 'USD', target: 'JPY' },
  { source: 'USD', target: 'CAD' },
  { source: 'EUR', target: 'CHF' },
  { source: 'GBP', target: 'EUR' },
  { source: 'USD', target: 'AUD' },
  { source: 'EUR', target: 'SEK' },
  { source: 'USD', target: 'CHF' },
] as const

// ── Application Domain ──────────────────────────────────────────────────────

export const APPLICATION_STATUSES = [
  'pending_review', 'under_review', 'approved', 'rejected', 'returned', 'expired',
] as const

export const APPLICATION_TYPES = [
  'new_merchant', 'capability_change', 'program_change', 'reactivation',
] as const

// ── Card Program Domain ─────────────────────────────────────────────────────

export const CARD_PROGRAM_STATUSES = [
  'active', 'inactive', 'draft', 'pending_approval', 'suspended',
] as const

export const CARD_PROGRAM_TYPES = ['credit', 'debit', 'prepaid', 'commercial', 'virtual'] as const

export const CARD_PROGRAM_NETWORKS = ['visa', 'mastercard', 'amex', 'discover'] as const

export const PROGRAM_PRODUCT_STATUSES = ['active', 'inactive', 'draft', 'deprecated'] as const
export const PROGRAM_PRODUCT_CATEGORIES = ['consumer', 'business', 'premium', 'basic'] as const

// ── Billing Schedule Domain ─────────────────────────────────────────────────

export const BILLING_SCHEDULE_STATUSES = ['active', 'draft', 'completed', 'archived'] as const

export const BILLING_INVOICE_STATUSES = [
  'pending_approval', 'draft', 'open', 'paid', 'partially_paid',
] as const

export const BILLING_PAYMENT_TERMS = ['7 Days', '14 Days', '30 Days', '60 Days'] as const

// ── Reserve / Payout Profile Domain ─────────────────────────────────────────

export const RESERVE_PROFILE_STATUSES = ['active', 'draft', 'archived'] as const
export const RESERVE_MODEL_TYPES = ['rolling', 'fixed', 'capped', 'up-front'] as const

export const PAYOUT_PROFILE_STATUSES = ['active', 'draft', 'archived'] as const
export const PAYOUT_MODEL_TYPES = ['same-day-payout', 'pass-through-payout'] as const

export const PAYOUT_FREQUENCIES = ['daily', 'weekly', 'bi-weekly', 'monthly'] as const

// ── FX Schedule Domain ──────────────────────────────────────────────────────

export const FX_SCHEDULE_STATUSES = ['active', 'draft', 'archived'] as const
export const FX_RATE_SOURCES = ['provider', 'manual'] as const

export const FX_VENDORS = [
  'Oanda', 'Reuters', 'Bloomberg', 'ECB', 'XE', 'CurrencyLayer',
] as const

// ── Shared Constants ────────────────────────────────────────────────────────

export const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'] as const

export const ALL_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'SEK', 'NOK', 'DKK',
  'SGD', 'HKD', 'NZD', 'PLN', 'CZK', 'HUF', 'BGN', 'RON',
] as const

export const TAG_POOL = [
  'seasonal', 'always-on', 'retention', 'acquisition', 'referral',
  'cross-sell', 'holiday', 'high-priority', 'high-roi', 'vip',
  'points', 'tier-upgrade', 'gamification', 'win-back', 'churn',
  'product-launch', 'draft', 'archived', 'exclusive',
] as const

export const CREATOR_NAMES = [
  'Sarah Chen', 'Michael Torres', 'Emily Park', 'David Kim',
  'Alex Johnson', 'Maria Santos', 'James Wright',
] as const

export const PROGRAM_NAMES = [
  'paylink_eu', 'paylink_us', 'paylink_global', 'marketplace_eu',
  'marketplace_us', 'enterprise_global', 'smb_starter',
] as const

export const REGION_NAMES = [
  'North America', 'Europe', 'Asia Pacific', 'Latin America',
  'Middle East', 'Africa', 'Global',
] as const

// ── Competitor Names ────────────────────────────────────────────────────────

export const COMPETITOR_NAMES = [
  { name: 'PayPal', program: 'PayPal Rewards' },
  { name: 'Stripe', program: 'Stripe Loyalty Suite' },
  { name: 'Square', program: 'Square Loyalty' },
  { name: 'Adyen', program: 'Adyen Engage' },
  { name: 'Block', program: 'Block Rewards' },
  { name: 'Worldpay', program: 'Worldpay Plus' },
  { name: 'Braintree', program: 'Braintree Points' },
  { name: 'Clover', program: 'Clover Rewards' },
] as const

export const REWARD_TYPE_LABELS = [
  'Cashback', 'Points', 'Stamp Card', 'Tiered Points',
  'Miles', 'Discount', 'Gift Card',
] as const

export const MEMBER_BASE_OPTIONS = [
  '2M+', '5M+', '8M+', '10M+', '12M+', '15M+', '20M+', '25M+',
] as const

// ── Competitive Strengths/Weaknesses Pools ──────────────────────────────────

export const STRENGTH_POOL = [
  'Broad merchant network', 'Instant cashback', 'No annual fee',
  'Developer-friendly API', 'Flexible reward rules', 'Real-time analytics',
  'Easy POS integration', 'Simple setup', 'Strong SMB presence',
  'Enterprise focus', 'Global reach', 'Advanced segmentation',
  'Mobile-first experience', 'Omnichannel support', 'Low transaction fees',
  'Strong brand recognition', 'AI-driven personalization',
] as const

export const WEAKNESS_POOL = [
  'Low reward rate', 'Limited partner offers', 'No tier system',
  'Complex setup', 'Higher merchant costs', 'Limited consumer brand',
  'Limited customization', 'Basic analytics', 'No tiered rewards',
  'High minimum spend', 'Slow onboarding', 'Enterprise pricing',
  'Poor mobile experience', 'Limited integrations', 'No real-time tracking',
  'Regional limitations', 'Outdated UI',
] as const

// ── Opportunity Templates ───────────────────────────────────────────────────

export const OPPORTUNITY_TITLES = [
  'Capture lapsed competitor customers',
  'Expand VIP tier benefits',
  'Launch seasonal cross-sell campaign',
  'Implement referral program upgrade',
  'Target at-risk high-value customers',
  'Introduce gamified milestone rewards',
  'Deploy mobile-first engagement',
  'Create co-branded partner campaign',
  'Optimize redemption flow conversion',
  'Launch win-back email sequence',
] as const

// ── Target Audience Templates ───────────────────────────────────────────────

export const TARGET_AUDIENCES = [
  'All active members',
  'All members',
  'Premium tier members',
  'VIP tier members',
  'Silver and Gold tier members',
  'New customers (first 30 days)',
  'Inactive 90+ days',
  'At-risk customers (declining activity)',
  'Standard tier members',
  'High-value customers ($1000+ spend)',
] as const

// ── Benchmark Metrics ───────────────────────────────────────────────────────

export const BENCHMARK_METRICS = [
  'Member Retention Rate',
  'Redemption Rate',
  'Average ROI',
  'Enrollment Conversion',
  'Points per Customer',
  'Churn Rate',
  'Average Basket Size',
  'Customer Lifetime Value',
  'Campaign Response Rate',
  'Net Promoter Score',
] as const

// ── Suspension/Termination Reasons ──────────────────────────────────────────

export const CUSTOMER_REASONS = [
  'Suspicious activity detected',
  'Excessive chargebacks',
  'Terms of service violation',
  'Identity verification failed',
  'Fraudulent transactions',
  'Account inactivity',
  'Customer requested closure',
  'Compliance review required',
  'High-risk behavior pattern',
  'Multiple failed payment attempts',
] as const
